import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  AppRoutes,
  FundService,
  IDEMPOTENT_WRITE_PATH,
  IdempotencyKeyService,
} from '@gs-2.0/core';
import { FundDisplay, Step } from '@gs-2.0/shared';
import { TransactionService } from '../../../_core/api/transaction.service';
import FundTypes from '../../../_core/constants/fund-types';
import PageTitles from '../../../_core/constants/page-titles';
import { SERVER_ERROR } from '../../../_core/constants/ServerError';
import Urls from '../../../_core/constants/urls';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import { GrantDataFee, GrantTransferData } from '../../../_core/models/grantData';
import { Fund } from '../../../_core/models/fund';
import { GlobalConfigApiService } from '../../../_core/api/global-config.service';
import { TitleService } from '../../../_core/services/title.service';
import { FeatureFlagService } from '../../../_core/services/feature-flag.service';
import { FeatureFlagKeys } from '../../../_core/models/FeatureFlags';

@Component({
  selector: 'app-grant',
  templateUrl: './grant.component.html',
  styleUrls: ['./grant.component.scss'],
  standalone: false,
})
export class GrantComponent implements OnInit, OnDestroy {
  urls = Urls;
  currentFund: Fund;
  grantFormData: any;
  grantFormValid = false;
  grantDataFee: GrantDataFee;

  grantDisplayData = new GrantTransferData();

  stepperSteps: Step[] = [
    { label: 'contribute.step.details', number: 1 },
    { label: 'contribute.step.summary', number: 2 },
    { label: 'contribute.step.confirmation', number: 3 },
  ];

  preSelectedCharityName: string | undefined;
  preSelectedProjectName: string | undefined;
  charityHash: string | undefined;
  lockCharityAfterSelection = false;
  currentStep = 1;
  showError = false;
  minimumGrantAmount = 0;
  isMinAmountError = false;
  loading = false;
  pageLoading = false;
  fundTypes = FundTypes;
  recurringEnabled = false;

  private recurringSubscription: Subscription;

  get activeStepIndex(): number {
    return this.currentStep - 1;
  }

  numberFormatConfig = { decimalSymbol: '.', digitGroupingSymbol: ',', digitGrouping: '123,456,789' };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: TitleService,
    protected fundService: FundService,
    private transactionService: TransactionService,
    private globalConfigApiService: GlobalConfigApiService,
    private featureFlagService: FeatureFlagService,
    private idempotencyKeyService: IdempotencyKeyService
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Opening the form starts a new submission, so no key held over from an earlier attempt
    // may be reused.
    this.idempotencyKeyService.release(IDEMPOTENT_WRITE_PATH.FUND_TO_FUND_TRANSFER);
    this.preSelectedCharityName = this.route.snapshot.queryParamMap.get('charityName') ?? undefined;
    this.preSelectedProjectName = this.route.snapshot.queryParamMap.get('projectName') ?? undefined;
    this.charityHash = this.route.snapshot.queryParamMap.get('charityHash') ?? undefined;
    this.lockCharityAfterSelection = this.route.snapshot.queryParamMap.get('lockCharity') === 'true';
    this.loadGlobalConfig();
    this.recurringSubscription = this.featureFlagService.hasFeatureFlag(FeatureFlagKeys.RecurringGrants).subscribe((enabled) => {
      this.recurringEnabled = enabled;
    });
    const fund = this.fundService.selectedFund();
    this.currentFund = fund as unknown as Fund;
    if (!this.currentFund) {
      this.pageLoading = true;
      this.fundService.loadFunds().then(() => {
        this.currentFund = this.fundService.selectedFund() as unknown as Fund;
        this.pageLoading = false;
        this.setPageTitle();
      });
    } else {
      this.setPageTitle();
    }
  }

  ngOnDestroy(): void {
    this.recurringSubscription?.unsubscribe();
  }

  private loadGlobalConfig(): void {
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config: any) => {
        //number format
        if (config?.response?.numberFormat) {
          this.numberFormatConfig = {
            decimalSymbol: config.response.numberFormat.decimalSymbol?.value || '.',
            digitGroupingSymbol: config.response.numberFormat.digitGroupingSymbol?.value || ',',
            digitGrouping: config.response.numberFormat.digitGrouping || '123,456,789',
          };
        }
        //minimum grant amount
        if(config?.response?.minimumGrantAmount) {
          this.minimumGrantAmount = config.response.minimumGrantAmount;
        }
      },
    });
  }

  setPageTitle(): void {
    if (!this.currentFund) return;
    this.currentFund.fundType === FundTypes.CHARITY
      ? this.titleService.titleChanged(PageTitles.GRANT_CHARITY)
      : this.titleService.titleChanged(PageTitles.GRANT_DAF);
  }

  computeDisplayData(): void {
    this.grantDisplayData = { ...this.grantFormData };
    this.grantDisplayData.privacy = this.grantFormData.privacy ? 'Yes' : 'No';
    this.grantDisplayData.isRecurring = this.grantFormData.timing === 'recurring';
    this.grantDisplayData.timing =
      this.grantFormData.timing === 'specificDate'
        ? this.grantFormData.date
        : 'As soon as possible';
  }

  goToSummary(): void {
    if (this.grantFormValid) {
      window.scrollTo(0, 0);
      this.computeDisplayData();
      this.currentStep = 2;
    }
  }

  goToForm(): void {
    window.scrollTo(0, 0);
    this.currentStep = 1;
    this.showError = false;
    this.isMinAmountError = false;
  }

  onSubmit(): void {
    this.loading = true;
    const payload = this.buildGrantPayload(this.grantFormData, this.currentFund);
    this.transactionService.submitTransactionFundToFund(payload).subscribe({
      next: () => {
        // Fund balance changed on the backend. Force-refresh so the next
        // dashboard visit sees up-to-date balances rather than the cached
        // pre-grant state (F2 otherwise skips the reload).
        this.fundService.loadFunds({ force: true });
        this.currentStep = 3;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        if (error.status === 409 && error.error?.message === SERVER_ERROR.NOT_MINIMUM_AMOUNT) {
          this.isMinAmountError = true;
        } else {
          this.showError = true;
        }
      },
    });
  }

  onFundChanged(fund: FundDisplay): void {
    this.fundService.setSelectedFund(fund.id);
    this.router.navigate([AppRoutes.FUND_DASHBOARD]);
  }

  goToPending(): void {
    this.router.navigate([`${Urls.APP}/${Urls.ACTIVITY}/${Urls.PENDING}`]);
  }

  goToDashboard(): void {
    this.router.navigate([`${Urls.FUND}/${Urls.DASHBOARD}`]);
  }

  reloadGrant(): void {
    const currentUrl = this.router.url;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([currentUrl]);
  }

  private buildGrantPayload(formData: any, fund: Fund): any {
    const isRecurring = formData.timing === 'recurring';
    const hasProject = !!formData.charityProjectCseg5;
    return {
      isFromGrantsTab: true,
      senderCseg5: fund.id || fund.cseg5,
      senderFundName: fund.fundName,
      receiverCseg5: hasProject ? formData.charityProjectCseg5 : formData.receiverCseg5,
      receiverFundName: hasProject ? formData.charityProjectNameOnly : formData.receiverFundName,
      // Fund numbers for the API Gateway path, alongside the cseg5 fields the NetSuite path needs. BT
      // resolves funds by fundNumber, and the backend needs one from the client as its lookup key —
      // cseg5 is legacy and absent for newer funds. Project grants keep the project's own fund number
      // when one is selected, mirroring the receiverCseg5 choice above.
      senderFundNumber: fund.fundNumber,
      receiverFundNumber: (hasProject ? formData.charityProjectFundNumber : formData.receiverFundNumber) ?? null,
      currency: this.fundService.selectedCurrency()?.symbol || fund.currencySymbol,
      // Explicit ISO code alongside `currency` above. `currency` is a legacy field the NetSuite path
      // reads as a numeric FundNumberCurrency id for non-BT tenants; `currencyCode` gives the backend an
      // unambiguous ISO code for the API Gateway path (used for both one-time and recurring grants alike)
      // instead of overloading `currency` with two different meanings.
      currencyCode: this.fundService.selectedCurrency()?.symbol || fund.currencySymbol,
      amount: formData.amount,
      date: SharedMethodsHelpers.dateToMilliseconds(formData.date, true),
      privacy: formData.privacy ? formData.privacy : false,
      charityInstruction: formData.charityInstruction ? formData.charityInstruction : '',
      additionalNotes: formData.additionalNotes,
      charityProject: hasProject || fund.fundType === FundTypes.CHARITY || fund.fundType === FundTypes.PROJECT,
      fundType: fund.fundType,
      isRecurring,
      recurrencePattern: isRecurring ? (formData.recurrencePattern ?? null) : null,
      startDate: isRecurring ? (formData.startDate ?? null) : null,
      endDate: isRecurring ? (formData.endDate ?? null) : null,
      timeZone: this.getTimezone(),
    };
  }

  private getTimezone(): string {
    const offset = -new Date().getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
    const mins = (Math.abs(offset) % 60).toString().padStart(2, '0');
    return `UTC${sign}${hours}:${mins}`;
  }
}
